import { _ } from "../../../core/polyglot/polyglot.js";
import {
	checkIcon,
	editRegularIcon,
	exclamationTriangleIcon,
} from "../../../core/elements/icons/index.js";
import { iconAndText } from "../../../core/elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import AlertMessageWidget from "../../../core/widgets/AlertMessageWidget/AlertMessageWidget.js";
import EmptyArchiveWidget from "../../../core/widgets/emptyArchive/EmptyArchiveWidget.js";
import FormulaTokenBoxRenderer from "./visitors/FormulaTokenBoxRenderer.js";
import FormulaTokenizationError from "../../../models/keyFigure/formula/visitors/FormulaTokenizationError.js";
import FormulaTokenizer from "../../../models/keyFigure/formula/visitors/FormulaTokenizer.js";
import FormulaTokenParser from "../../../models/keyFigure/formulaToken/visitors/FormulaTokenParser.js";
import NaFormulaNode from "../../../models/keyFigure/formula/NaFormulaNode.js";

/**
 * `KeyFigureFormulaEditorWidget` renders a formula tree as a flat list of
 * tokens.
 *
 * Tokens can be added, removed or edited, after which the formula is updated.
 * A formula update results in a new formula being built, as formula nodes are
 * immutable.
 *
 * Each time the formula is updated, the event `formulaUpdated` is fired with
 * the new formula.
 *
 * Drag&Drop is supported in the formula editor, making it easy to move tokens.
 *
 * @param {AbstractFormulaNode} spec.formula - Formula to be edited.
 * @param {Dimension} spec.dimensionTree - Tree of dimensions that can be used by
 * formula nodes.
 */
export default class KeyFigureFormulaEditorWidget extends Widget {
	constructor({ formula, dimensionTree }) {
		super(...arguments);

		this._selectedToken = null;
		this._originalFormulaIsEditable = null;

		this.setFormula(formula);

		this._dimensionTree = dimensionTree;

		this.tokenSelected = this._events.createEvent();
		this.formulaUpdated = this._events.createEvent();
	}

	setFormula(formula) {
		this._formula = formula;
		this._selectedToken = null;

		if (this._formula) {
			this._tokens = this._tokenize(this._formula);
		}

		this.update();
	}

	validate({ collector }) {
		if (this._isFormulaValid()) {
			return;
		}
		collector.addError(_("An invalid formula cannot be saved"));
	}

	addToken(token) {
		if (this._selectedToken) {
			// Insert token after the selected one
			this._tokens.splice(this._indexOfSelectedToken() + 1, 0, token);
		} else {
			this._tokens.push(token);
		}

		this.selectToken(token);
		this.updateFormula();
	}

	deleteToken(token) {
		this._tokens = this._tokens.filter((t) => t !== token);
		this.selectLastToken();
		this.updateFormula();
	}

	updateFormula() {
		this._parseFormula();
		this.update();
	}

	getSelectedToken() {
		return this._selectedToken;
	}

	selectToken(token) {
		this._selectedToken = token;
		this.tokenSelected.trigger(token);
		this.update();
	}

	selectLastToken() {
		this.selectToken(this._tokens.at(-1));
	}

	renderContentOn(html) {
		if (!this._originalFormulaIsEditable) {
			this._renderNonEditableMessageOn(html);
		}

		if (this._isEmpty()) {
			this._renderEmptyOn(html);
			return;
		}

		html.div(
			{ klass: "kpi-formula-editor", "data-test": "kpi-formula-editor" },
			this._renderTokensOn.bind(this),
			this._renderFormulaStateOn.bind(this),
		);
	}

	_renderEmptyOn(html) {
		html.render(
			new EmptyArchiveWidget({
				logo: editRegularIcon,
				title: _("Let's get started!"),
				subtitle: _("Add your first component on the right."),
			}),
		);
	}

	_renderTokensOn(html) {
		const renderer = new FormulaTokenBoxRenderer({
			dimensionTree: this._dimensionTree,
			selectedToken: this._selectedToken,
			onTokenSelected: this.selectToken.bind(this),
		});

		html.div({ klass: "tokens" }, renderer.visitAll(this._tokens));
	}

	_renderFormulaStateOn(html) {
		const isFormulaValid = this._isFormulaValid();

		html.div(
			{
				klass: [
					"formula-state",
					{ valid: isFormulaValid, invalid: !isFormulaValid },
				],
			},
			this._isFormulaValid()
				? iconAndText({ icon: checkIcon }, _("Valid formula"))
				: iconAndText(
						{ icon: exclamationTriangleIcon },
						_("Invalid formula"),
					),
		);
	}

	_renderNonEditableMessageOn(html) {
		html.render(
			new AlertMessageWidget({
				type: "warning",
				title: _("This default KPI cannot be edited."),
				content: [
					_(
						"Instead you can create your own custom KPI and tailor it to your needs.",
					),
				],
			}),
		);
	}

	_didAttach() {
		super._didAttach();

		this._setupDragAndDrop();
	}

	_setupDragAndDrop() {
		this.asJQuery()
			.find(".tokens")
			.sortable({
				placeholder: "sortable-placeholder",
				axis: "x",
				opacity: "0.8",
				update: this._sortTokens.bind(this),
			});
	}

	_sortTokens() {
		this._tokens = this.asJQuery()
			.find(".token")
			.get()
			.map((div) =>
				this._tokens.find((token) => token.getId() === div.id),
			);

		this.updateFormula();
	}

	_parseFormula() {
		try {
			const formula = new FormulaTokenParser().parse(this._tokens);
			this._formula = formula;
			this.formulaUpdated.trigger(this._formula);
		} catch {
			this._formula = new NaFormulaNode();
		}
	}

	_indexOfSelectedToken() {
		return this._tokens.indexOf(this._selectedToken);
	}

	_isFormulaValid() {
		return this._formula.isValid();
	}

	_isEmpty() {
		return this._tokens.length === 0;
	}

	_tokenize(node) {
		try {
			this._originalFormulaIsEditable = true;
			return new FormulaTokenizer().tokenize(node);
		} catch (error) {
			if (!(error instanceof FormulaTokenizationError)) {
				throw error;
			}

			this._originalFormulaIsEditable = false;

			return [];
		}
	}
}
